import Toast from "react-native-toast-message";
import { supabase } from "./client";
import { getCurrentOrgID } from "./organization";
import ErrorHandler from "@/utils/ErrorHandler";



/**
 * Retrieves all quick filters for the current org.
 * @returns {Promise<object[]>} List of quick filters.
 */
const getQuickFilters = async () => {
    const orgID = await getCurrentOrgID(); 

    const {data, error} = await supabase.from("QuickFilters").select("*").eq("organizationID", orgID).order('name')

    if(error){
        ErrorHandler(Error(error.message), {query: "getQuickFilters", orgID, ...error}, `Something went wrong fetching the custom filters`)
        return [];
    }

    return data;
}

/**
 * Creates a new quick filter.
 * @param {object} params - The parameters for the function.
 * @param {object} params.details - Details for the new filter.
 * @returns {Promise<object|boolean>} The created filter or false on error.
 */
const createQuickFilters = async ({details}) => {
    const orgID = await getCurrentOrgID(); 

    const insert = {
        organizationID: orgID,
        name: details.name,
        logic: details.filterLogic || "AND",
        filterJSON: details.filterJSON
    }

    const { data, error } = await supabase.from("QuickFilters").insert(insert).select().single();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "createQuickFilters", input: insert, ...error}, `Something went wrong creating that filter`)
        return false;
    }

    Toast.show({
        type: 'success',
        text2: `New quick filter has been created successfully!`
    });

    return data;
}

/**
 * Updates an existing quick filter.
 * @param {object} params - The parameters for the function.
 * @param {object} params.details - Updated filter details.
 * @returns {Promise<object|boolean>} The updated filter or false on error.
 */
const updateQuickFilters = async ({details}) => {
    const update = {
        name: details.name,
        logic: details.filterLogic || "AND",
        filterJSON: details.filterJSON
    }

    const { data, error } = await supabase.from("QuickFilters").update(update).eq("id", details.id).select().single();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "updateQuickFilters", input: update, ...error}, `Something went wrong updating that filter`)
        return false;
    }

    Toast.show({
        type: 'success',
        text2: `This quick filter has been updated successfully!`
    });

    return data;
}

/**
 * Deletes a quick filter by ID.
 * @param {string|number} id - The ID of the filter to delete.
 * @returns {Promise<boolean|void>} False on error, else void.
 */
const deleteQuickFilters = async (id) => {
    const { error } = await supabase.from("QuickFilters").delete().eq("id", id);

    if(error){
        ErrorHandler(Error(error.message), {mutation: "deleteQuickFilters", input: id, ...error}, `Something went wrong deleting that filter`)
        return false;
    }

    Toast.show({
        type: 'success',
        text2: `Quick filter has been deleted successfully!`
    });
}

export {
    getQuickFilters,
    createQuickFilters,
    updateQuickFilters,
    deleteQuickFilters
}